import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckBadgeIcon, ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import PurchaseOrderStatusChip from '../features/purchaseOrders/PurchaseOrderStatusChip';

const PORTAL_MENU_GAP_PX = 8;
const EXPANDED_MENU_MIN_WIDTH_PX = 240;
const EXPANDED_MENU_MAX_WIDTH_PX = 360;

export const SelectDropdown = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  testId,
  searchable = false,
  menuSearchable = false,
  selectClassname = "",
  selectedItemValueClassname = "",
  isStatuses,
  SelectedValueIconComponent = null,
  selectedValueClassname = "",
  itemValueClassname = "",
  dropdownClassname = "",
  searchPlaceholder = 'Type to search...',
  minCharsToSearch = 0,
  debounceMs = 300,
  onSearch = null,
  disabled = false,
  renderOption,
  multiple = false,
  showSaveCurrentView = false,
  onSaveCurrentView,
  onDeleteOption,
  saveCurrentViewLabel = 'Save Current View',
  dropdownListClassname = "",
  tooltip = undefined,
  portal = false,
  expandMenuWidth = false,
  showChevron = true,
  mainWrapperClassname = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedItemRef = useRef(null);
  const portalPanelRef = useRef(null);
  const [portalMenuRect, setPortalMenuRect] = useState(null);
  const [expandedMenuWidth, setExpandedMenuWidth] = useState(null);

  const measureExpandedMenuWidth = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const triggerStyle = window.getComputedStyle(el);
    context.font = triggerStyle.font || '14px sans-serif';
    const widestLabel = options.reduce((widest, option) => {
      const labelWidth = context.measureText(String(option.label ?? '')).width;
      return Math.max(widest, labelWidth);
    }, 0);
    const hasTrailingBadge = options.some((option) => option.filled);
    return Math.ceil(widestLabel + 32 + (hasTrailingBadge ? 24 : 0));
  }, [options]);

  const updatePortalPosition = useCallback(() => {
    if ((!portal && !expandMenuWidth) || searchable) return;
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const measuredWidth = measureExpandedMenuWidth();
    const maxWidth = Math.max(r.width, window.innerWidth - r.left - PORTAL_MENU_GAP_PX);
    const width = Math.min(
      Math.max(r.width, measuredWidth, EXPANDED_MENU_MIN_WIDTH_PX),
      EXPANDED_MENU_MAX_WIDTH_PX,
      maxWidth,
    );
    setExpandedMenuWidth(width);
    setPortalMenuRect({
      top: r.bottom + PORTAL_MENU_GAP_PX,
      left: portal ? Math.max(PORTAL_MENU_GAP_PX, Math.min(r.left, window.innerWidth - width - PORTAL_MENU_GAP_PX)) : r.left,
      width,
    });
  }, [expandMenuWidth, measureExpandedMenuWidth, portal, searchable]);

  useLayoutEffect(() => {
    if (!isOpen || (!portal && !expandMenuWidth) || searchable) {
      setPortalMenuRect(null);
      setExpandedMenuWidth(null);
      return;
    }
    updatePortalPosition();
    const onMove = () => updatePortalPosition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [expandMenuWidth, isOpen, portal, searchable, updatePortalPosition]);
  
  useEffect(() => {
    if (isOpen && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTriggerTree = dropdownRef.current?.contains(event.target);
      const inPortalMenu = portal && portalPanelRef.current?.contains(event.target);
      if (inTriggerTree || inPortalMenu) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [portal]);

  useEffect(() => {
    if (!searchable || typeof onSearch !== 'function') return;
    if (!query.length || query.length < minCharsToSearch) return;
    const timer = setTimeout(() => {
      onSearch(query);
      setIsOpen(true);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, searchable, onSearch, minCharsToSearch, debounceMs]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }
    if (menuSearchable) {
      searchInputRef.current?.focus();
    }
  }, [isOpen, menuSearchable, portalMenuRect]);

  const selectedValues = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value
      ? [value]
      : [];

  const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));
  const searchableOptions = menuSearchable && query.trim()
    ? options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;
  const displayOptions = multiple
    ? searchableOptions.filter(opt => !selectedValues.includes(opt.value))
    : searchableOptions;

  const handleOptionClick = (option) => {
    setIsOpen(false);
    if (multiple) {
      onChange([...selectedValues, option.value]);
      setQuery('');
    } else {
      onChange(option.value);
      setQuery('');
    }
  };

  const renderButtonLabel = () => {
    if (selectedOptions.length > 0) {
      return selectedOptions.map(opt => opt.label).join(', ');
    }
    return placeholder;
  };

  const handleSaveCurrentView = () => {
    if (typeof onSaveCurrentView === 'function') {
      onSaveCurrentView();
    }
    setIsOpen(false);
  };

  const renderMenuBody = (emptyListHint) => (
    <>
      {menuSearchable && (
        <div className="border-b border-gray-200 p-2">
          <input
            ref={searchInputRef}
            type="text"
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-gray-500"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      <ul className={`max-h-48 overflow-y-auto ${dropdownListClassname || ""}`} ref={listRef}>
        {displayOptions.length > 0 ? (
          displayOptions.map((option, index) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <li
                key={index}
                onClick={() => handleOptionClick(option)}
                ref={isSelected ? selectedItemRef : null}
                title={expandMenuWidth ? option.label : undefined}
                className={`
                  px-2 py-1 cursor-pointer hover:bg-blue-50 flex min-w-0 justify-between
                  ${itemValueClassname || ""}
                  ${isSelected ? `${isStatuses ? "" : "bg-blue-200"} font-semibold ${selectedItemValueClassname || ""}` : ''}
                `}
              >
                <span className={expandMenuWidth ? "min-w-0 flex-1 truncate whitespace-nowrap" : ""}>
                  {isStatuses ? (
                    <PurchaseOrderStatusChip
                      status={option.value}
                      className="!m-0"
                    />
                  ) : (
                    renderOption ? renderOption(option) : option.label
                  )}
                </span>
                {typeof onDeleteOption === 'function' && option.deletable && (
                  <button
                    type="button"
                    aria-label={`Delete ${option.label}`}
                    className="ml-2 shrink-0 rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteOption(option);
                      setIsOpen(false);
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
                {option.filled && (
                  <CheckBadgeIcon className="h-5 w-5 text-green-700" />
                )}
              </li>
            );
          })
        ) : (
          <li className="px-4 py-2 text-gray-500">
            {emptyListHint}
          </li>
        )}
      </ul>
      {showSaveCurrentView && (
        <button
          type="button"
          onClick={handleSaveCurrentView}
          className="w-full px-4 py-2 border-t border-gray-200 text-left text-sm font-medium text-primary hover:!bg-blue-50 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          {saveCurrentViewLabel}
        </button>
      )}
    </>
  );

  return (
    <div className={`relative w-full ${mainWrapperClassname || ""}`} ref={dropdownRef} data-testid={testId}>
      {searchable ? (
        <input
          type="text"
          className="w-full border rounded p-2 text-sm"
          placeholder={selectedOptions.length > 0 && !query ? renderButtonLabel() : searchPlaceholder}
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => setQuery(e.target.value)}
        />
      ) : (
        <button
          ref={triggerRef}
          onClick={() => setIsOpen((prev) => !prev)}
          type="button"
          disabled={disabled}
          title={
            tooltip !== undefined && tooltip !== null && String(tooltip).trim() !== ""
              ? String(tooltip)
              : renderButtonLabel()
          }
          className={`flex justify-between disabled:!bg-gray-200 items-center bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg outline-none block w-full p-2.5 ${selectClassname || ""}`}
        >
          {isStatuses ? (
            <PurchaseOrderStatusChip
              status={value}
              className={`!m-0 ${selectedValueClassname || ""}`}
            />
          ) : (
            <span className={`flex flex-row items-center gap-2 text-gray-900 text-sm truncate ${selectedValueClassname || ""}`}>
              {SelectedValueIconComponent && (
                <SelectedValueIconComponent />
              )}
              {renderButtonLabel()}
            </span>
          )}
          <div className='flex flex-row gap-2'>
            {((options || []).some((option) => (selectedOptions || []).includes(option) && option.filled)) && (
              <CheckBadgeIcon className="h-5 w-5 text-green-700" />
            )}
            {showChevron && <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />}
          </div>
        </button>
      )}

      {isOpen && (!portal || searchable) && (
        <div
          className={`absolute z-20 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg ${dropdownClassname || ""}`}
          style={expandMenuWidth && expandedMenuWidth ? { width: expandedMenuWidth, maxWidth: 'calc(100vw - 16px)' } : undefined}
        >
          {renderMenuBody(searchable ? (query.length < minCharsToSearch ? `Type at least ${minCharsToSearch} chars` : 'No options found') : menuSearchable ? 'No options found' : 'No options')}
        </div>
      )}
      {isOpen && portal && !searchable && portalMenuRect &&
        createPortal(
          <div
            ref={portalPanelRef}
            className={`fixed z-[120] bg-white border border-gray-300 rounded-md shadow-lg ${dropdownClassname || ""}`}
            style={{
              top: portalMenuRect.top,
              left: portalMenuRect.left,
              width: portalMenuRect.width,
            }}
          >
            {renderMenuBody('No options')}
          </div>,
          document.body
        )}
    </div>
  );
};